import { FreeCamera } from "@babylonjs/core/Cameras/freeCamera";
import { UniversalCamera } from "@babylonjs/core/Cameras/universalCamera";
import { Ray } from "@babylonjs/core/Culling/ray";
import type { Engine } from "@babylonjs/core/Engines/engine";
import { HemisphericLight } from "@babylonjs/core/Lights/hemisphericLight";
import { StandardMaterial } from "@babylonjs/core/Materials/standardMaterial";
import { DynamicTexture } from "@babylonjs/core/Materials/Textures/dynamicTexture";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Color3, Color4 } from "@babylonjs/core/Maths/math.color";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";
import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { MeshBuilder } from "@babylonjs/core/Meshes/meshBuilder";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { Scene } from "@babylonjs/core/scene";
import type { Direction, DirectionColor, DirectionColorMap, TargetDefinition } from "../core/types";
import { DIRECTIONS } from "../core/types";
import type { SceneCandidate } from "../scene-training/types";
import type { EntranceAuditDescriptor, ScreenshotSignature } from "../diagnostics/leakage-audit";
import { analyzeScreenshotDataUrl } from "../diagnostics/leakage-audit";
import type { PerformanceTier } from "../diagnostics/FramePerformanceMonitor";
import { RevealContentManager, type RevealResult } from "../visual/RevealContentManager";
import { resolveMazeMovement } from "./maze-collision";

const DIRECTION_VECTORS: Readonly<Record<Direction, Vector3>> = {
  north: new Vector3(0, 0, 1),
  east: new Vector3(1, 0, 0),
  south: new Vector3(0, 0, -1),
  west: new Vector3(-1, 0, 0),
};

const COLOR_HEX: Readonly<Record<DirectionColor, string>> = {
  cyan: "#28bec8",
  orange: "#dc9146",
  purple: "#b491e1",
  lime: "#8cb950",
};

export class WhiteboxMaze {
  readonly scene: Scene;
  readonly camera: UniversalCamera;
  private readonly mazeRoot: TransformNode;
  private readonly instructionRoot: TransformNode;
  private readonly content: RevealContentManager;
  private readonly gates = new Map<Direction, Mesh>();
  private readonly gateMaterials = new Map<Direction, StandardMaterial>();
  private readonly photoPlanes = new Map<Direction, Mesh>();
  private readonly photoMaterials = new Map<Direction, StandardMaterial>();
  private sceneRevealRoot: TransformNode | null = null;
  private sceneExplorationHalfExtent: number | null = null;
  private readonly activeDirections = new Set<Direction>();
  private focused: Direction | null = null;
  private pending: Direction | null = null;
  private opened: Direction | null = null;

  constructor(canvas: HTMLCanvasElement, engine: Engine) {
    this.scene = new Scene(engine);
    this.scene.clearColor = Color4.FromHexString("#0c141cff");
    this.scene.collisionsEnabled = true;
    this.camera = new UniversalCamera("participant-camera", new Vector3(0, 1.65, 0), this.scene);
    this.camera.minZ = 0.05;
    this.camera.fov = 1.18;
    this.camera.inertia = 0;
    this.camera.ellipsoid = new Vector3(0.25, 0.82, 0.25);
    // Movement uses the maze's deterministic planar resolver. Babylon's camera
    // collision coordinator can synchronously stall the render loop on the
    // first externally injected movement delta in some browser/GPU paths.
    this.camera.checkCollisions = false;
    this.scene.activeCamera = this.camera;
    new HemisphericLight("ambient", Vector3.Up(), this.scene).intensity = 0.86;
    // Low-intensity upward fill keeps indoor ceiling undersides readable when
    // the participant is standing inside the revealed room/hall.
    new HemisphericLight("indoor-fill", Vector3.Down(), this.scene).intensity = 0.24;

    this.mazeRoot = new TransformNode("maze-root", this.scene);
    this.instructionRoot = new TransformNode("instruction-root", this.scene);
    this.content = new RevealContentManager(this.scene);
    this.buildMaze();
    this.buildInstructionRoom();
    this.showInstructionRoom(true);

    canvas.addEventListener("contextmenu", (event) => event.preventDefault());
  }

  initializeContent(): Promise<string> {
    return this.content.initialize();
  }

  applyContentQualityTier(tier: number): void {
    this.content.setQualityTier(tier);
  }

  applyPerformanceTier(tier: PerformanceTier): void {
    const scaling = [1, 1.2, 1.4, 1.6][tier] ?? 1.6;
    this.scene.getEngine().setHardwareScalingLevel(scaling);
    this.applyContentQualityTier(tier);
  }

  showInstructionRoom(visible: boolean): void {
    this.instructionRoot.setEnabled(visible);
    this.mazeRoot.setEnabled(!visible);
    if (visible) {
      this.camera.position.set(0, 1.65, -20.4);
      this.camera.rotation.set(0, 0, 0);
    } else {
      this.resetParticipant();
    }
  }

  configureJunction(colors: DirectionColorMap, activeDirections: readonly Direction[]): void {
    this.mazeRoot.setEnabled(true);
    this.activeDirections.clear();
    activeDirections.forEach((direction) => this.activeDirections.add(direction));
    this.opened = null;
    this.focused = null;
    this.pending = null;
    this.disposeReveal();
    this.gates.forEach((gate, direction) => {
      gate.setEnabled(true);
      gate.checkCollisions = true;
      const material = this.gateMaterials.get(direction);
      if (!material) return;
      material.diffuseColor = Color3.FromHexString(COLOR_HEX[colors[direction]]);
      material.emissiveColor = material.diffuseColor.scale(0.16);
      material.alpha = this.activeDirections.has(direction) ? 0.88 : 0.16;
    });
  }

  configureSceneDecision(colors: DirectionColorMap, candidates: readonly SceneCandidate[]): void {
    this.configureJunction(colors, candidates.map((candidate) => candidate.direction));
    const byDirection = new Map(candidates.map((candidate) => [candidate.direction, candidate]));
    this.photoPlanes.forEach((plane, direction) => {
      const candidate = byDirection.get(direction);
      plane.setEnabled(Boolean(candidate));
      const material = this.photoMaterials.get(direction);
      if (!candidate || !material) return;
      material.diffuseTexture?.dispose();
      // Babylon's image upload path expects browser images to be Y-inverted.
      // Passing false here displayed the photos upside down on desktop/WebGL.
      const texture = new Texture(candidate.photo.url, this.scene, true, true, Texture.TRILINEAR_SAMPLINGMODE);
      texture.hasAlpha = false;
      material.diffuseTexture = texture;
      material.emissiveColor = Color3.White();
      material.diffuseColor = Color3.White();
    });
  }

  resetParticipant(): void {
    const camera = this.getParticipantCamera();
    camera.position.x = 0;
    camera.position.z = 0;
    if (camera === this.camera) {
      camera.position.y = 1.65;
      camera.rotation.x = 0;
    }
  }

  moveParticipant(displacement: Vector3): void {
    const camera = this.getParticipantCamera();
    if (this.sceneExplorationHalfExtent !== null) {
      const limit = this.sceneExplorationHalfExtent;
      camera.position.x = Math.max(-limit, Math.min(limit, camera.position.x + displacement.x));
      camera.position.z = Math.max(-limit, Math.min(limit, camera.position.z + displacement.z));
      if (camera === this.camera) camera.position.y = 1.65;
      return;
    }
    const next = resolveMazeMovement(
      { x: camera.position.x, z: camera.position.z },
      { x: displacement.x, z: displacement.z },
      this.opened,
    );
    camera.position.x = next.x;
    camera.position.z = next.z;
    if (camera === this.camera) camera.position.y = 1.65;
  }

  getFocusedDirection(): Direction | null {
    const camera = this.getParticipantCamera();
    if (Math.hypot(camera.position.x, camera.position.z) > 1.25) return null;
    const forward = camera.getDirection(Vector3.Forward());
    forward.y = 0;
    if (forward.lengthSquared() < 0.001) return null;
    forward.normalize();

    let best: Direction | null = null;
    let bestAngle = Number.POSITIVE_INFINITY;
    for (const direction of DIRECTIONS) {
      if (!this.activeDirections.has(direction)) continue;
      const angle = Math.acos(Math.max(-1, Math.min(1, Vector3.Dot(forward, DIRECTION_VECTORS[direction]))));
      if (angle < bestAngle) {
        bestAngle = angle;
        best = direction;
      }
    }
    return bestAngle <= (20 * Math.PI) / 180 ? best : null;
  }

  setFocused(direction: Direction | null): void {
    this.focused = direction;
    this.refreshGateEmissive();
  }

  setPending(direction: Direction | null): void {
    this.pending = direction;
    this.refreshGateEmissive();
  }

  openDirection(direction: Direction): void {
    this.opened = direction;
    this.pending = null;
    this.gates.get(direction)?.setEnabled(false);
  }

  getOpenedCorridorDistance(): number {
    if (!this.opened) return 0;
    return Vector3.Dot(this.getParticipantCamera().position, DIRECTION_VECTORS[this.opened]) - 3;
  }

  showReveal(target: TargetDefinition): Promise<RevealResult> {
    if (!this.opened) return Promise.reject(new Error("No corridor is open for reveal"));
    const direction = DIRECTION_VECTORS[this.opened];
    return this.content.reveal(target, direction.scale(6.6), Math.atan2(direction.x, direction.z));
  }

  async showSceneReveal(sceneId: string): Promise<{ readonly sceneId: string; readonly meshCount: number; readonly researchEligible: false }> {
    this.disposeSceneReveal();
    this.mazeRoot.setEnabled(false);
    const root = new TransformNode("scene-reveal-root", this.scene);
    this.sceneRevealRoot = root;
    const addBox = (
      name: string,
      size: { width: number; height: number; depth: number },
      position: Vector3,
      hex: string,
      collision = true,
    ): Mesh => {
      const mesh = MeshBuilder.CreateBox(name, size, this.scene);
      mesh.position.copyFrom(position);
      mesh.material = this.material(`${name}-scene`, hex);
      mesh.parent = root;
      mesh.checkCollisions = collision;
      mesh.isPickable = collision;
      return mesh;
    };
    const addTree = (name: string, position: Vector3, leafHex: string): void => {
      const trunk = MeshBuilder.CreateCylinder(`${name}-trunk`, { height: 2.2, diameter: 0.28, tessellation: 8 }, this.scene);
      trunk.position.copyFrom(position.add(new Vector3(0, 1.1, 0)));
      trunk.material = this.material(`${name}-trunk`, "#5a4230");
      trunk.parent = root;
      trunk.checkCollisions = true;
      trunk.isPickable = true;
      const crown = MeshBuilder.CreateSphere(`${name}-crown`, { diameter: 1.5, segments: 7 }, this.scene);
      crown.scaling.y = 1.25;
      crown.position.copyFrom(position.add(new Vector3(0, 2.45, 0)));
      crown.material = this.material(`${name}-crown`, leafHex);
      crown.parent = root;
    };
    const addFloorStrip = (name: string, width: number, depth: number, position: Vector3, hex: string): void => {
      addBox(name, { width, height: 0.018, depth }, position, hex, false);
    };
    const addWoodFloorDetail = (): void => {
      for (let x = -4; x <= 4; x += 1) addFloorStrip(`wood-plank-long-seam-${x}`, 0.026, 9.7, new Vector3(x, 0.018, 0), "#5b3a24");
      for (let z = -4; z <= 4; z += 1.6) addFloorStrip(`wood-plank-cross-seam-${z}`, 9.7, 0.022, new Vector3(0, 0.02, z), "#6c4529");
      [-3.5, -1.5, 0.5, 2.5].forEach((x, index) => {
        addFloorStrip(`wood-plank-warm-band-${index}`, 0.44, 9.55, new Vector3(x, 0.021, 0), "#9b6b40");
      });
    };
    const addTileFloorDetail = (): void => {
      for (let x = -4.4; x <= 4.4; x += 1.1) addFloorStrip(`tile-grout-x-${x.toFixed(1)}`, 0.028, 9.8, new Vector3(x, 0.018, 0), "#8e8b83");
      for (let z = -4.4; z <= 4.4; z += 1.1) addFloorStrip(`tile-grout-z-${z.toFixed(1)}`, 9.8, 0.028, new Vector3(0, 0.019, z), "#8e8b83");
      [-2.2, 0, 2.2].forEach((x, index) => {
        addFloorStrip(`tile-cool-highlight-${index}`, 0.35, 9.55, new Vector3(x, 0.02, 0), "#d8d4c9");
      });
    };
    const addGravelDetail = (): void => {
      const pebbleMaterial = this.material("reveal-gravel-pebble", "#b8b1a2");
      const pebblePositions = [
        [-0.9, -5.1], [0.75, -4.4], [-0.25, -3.5], [1.02, -2.7], [-0.82, -1.7], [0.42, -0.8],
        [-0.36, 0.15], [0.98, 0.95], [-1.05, 1.8], [0.16, 2.55], [0.82, 3.42], [-0.58, 4.35],
      ] as const;
      pebblePositions.forEach(([x, z], index) => {
        const pebble = MeshBuilder.CreateCylinder(`reveal-gravel-pebble-${index}`, {
          diameter: 0.09 + (index % 3) * 0.035,
          height: 0.035,
          tessellation: 6,
        }, this.scene);
        pebble.position.set(x, 0.105, z);
        pebble.scaling.x = 1.3 + (index % 2) * 0.25;
        pebble.rotation.y = index * 0.73;
        pebble.material = pebbleMaterial;
        pebble.parent = root;
      });
    };
    const addLeafDetail = (): void => {
      const colors = ["#c17a34", "#a85a2e", "#d09a3d", "#8d4f32"] as const;
      const leafPositions = [
        [-0.95, -5.0], [-0.25, -4.2], [0.86, -3.35], [0.28, -2.45], [-0.7, -1.58], [0.98, -0.62],
        [-0.42, 0.3], [0.64, 1.08], [-1.02, 1.85], [0.1, 2.7], [0.88, 3.52], [-0.55, 4.45],
      ] as const;
      leafPositions.forEach(([x, z], index) => {
        const leaf = MeshBuilder.CreateBox(`reveal-fallen-leaf-${index}`, { width: 0.28, height: 0.018, depth: 0.11 }, this.scene);
        leaf.position.set(x, 0.105, z);
        leaf.rotation.y = index * 0.91;
        leaf.material = this.material(`reveal-fallen-leaf-${index}`, colors[index % colors.length] ?? "#c17a34");
        leaf.parent = root;
      });
    };

    if (sceneId === "scene.wood_living_room.v1") {
      this.sceneExplorationHalfExtent = 4.55;
      addBox("reveal-wood-floor", { width: 10, height: 0.12, depth: 10 }, new Vector3(0, -0.06, 0), "#8f6039", false);
      addWoodFloorDetail();
      addBox("reveal-room-wall-n", { width: 10, height: 3.2, depth: 0.18 }, new Vector3(0, 1.6, 4.9), "#d4cbbd");
      addBox("reveal-room-wall-s", { width: 10, height: 3.2, depth: 0.18 }, new Vector3(0, 1.6, -4.9), "#d4cbbd");
      addBox("reveal-room-wall-e", { width: 0.18, height: 3.2, depth: 10 }, new Vector3(4.9, 1.6, 0), "#c9d1d0");
      addBox("reveal-room-wall-w", { width: 0.18, height: 3.2, depth: 10 }, new Vector3(-4.9, 1.6, 0), "#c9d1d0");
      addBox("reveal-room-ceiling", { width: 10, height: 0.12, depth: 10 }, new Vector3(0, 3.2, 0), "#e4dfd6", false);
      addBox("reveal-sofa-seat", { width: 3.1, height: 0.5, depth: 0.9 }, new Vector3(-2.4, 0.48, 3.65), "#697b80");
      addBox("reveal-sofa-back", { width: 3.1, height: 1.1, depth: 0.3 }, new Vector3(-2.4, 1.05, 4.05), "#697b80");
      addBox("reveal-rug", { width: 4.2, height: 0.04, depth: 2.4 }, new Vector3(0.5, 0.04, 0.6), "#81939a", false);
      addBox("reveal-table-top", { width: 1.9, height: 0.12, depth: 0.9 }, new Vector3(1.1, 0.72, 1.15), "#68462f");
      for (const [x, z] of [[0.35, 0.85], [1.85, 0.85], [0.35, 1.45], [1.85, 1.45]] as const) {
        addBox(`reveal-table-leg-${x}-${z}`, { width: 0.12, height: 0.68, depth: 0.12 }, new Vector3(x, 0.34, z), "#68462f");
      }
    } else if (sceneId === "scene.tile_exhibition_hall.v1") {
      this.sceneExplorationHalfExtent = 4.55;
      addBox("reveal-tile-floor", { width: 10, height: 0.12, depth: 10 }, new Vector3(0, -0.06, 0), "#c5c0b5", false);
      addTileFloorDetail();
      addBox("reveal-hall-wall-n", { width: 10, height: 3.8, depth: 0.18 }, new Vector3(0, 1.9, 4.9), "#e2e0da");
      addBox("reveal-hall-wall-s", { width: 10, height: 3.8, depth: 0.18 }, new Vector3(0, 1.9, -4.9), "#e2e0da");
      addBox("reveal-hall-wall-e", { width: 0.18, height: 3.8, depth: 10 }, new Vector3(4.9, 1.9, 0), "#d6d9da");
      addBox("reveal-hall-wall-w", { width: 0.18, height: 3.8, depth: 10 }, new Vector3(-4.9, 1.9, 0), "#d6d9da");
      addBox("reveal-hall-ceiling", { width: 10, height: 0.12, depth: 10 }, new Vector3(0, 3.8, 0), "#f0eee8", false);
      const exhibits = [[-2.8, -2.6], [2.8, -2.6], [-2.8, 2.6], [2.8, 2.6]] as const;
      exhibits.forEach(([x, z], index) => {
        addBox(`reveal-pedestal-${index}`, { width: 0.9, height: 0.9, depth: 0.9 }, new Vector3(x, 0.45, z), "#e0ddd5");
        const art = MeshBuilder.CreatePolyhedron(`reveal-art-${index}`, { type: index % 3, size: 0.52 }, this.scene);
        art.position.set(x, 1.35, z);
        art.material = this.material(`reveal-art-${index}`, ["#54798c", "#985747", "#b58442", "#617b5d"][index] ?? "#54798c");
        art.parent = root;
      });
    } else if (sceneId === "scene.gravel_park_path.v1") {
      this.sceneExplorationHalfExtent = 6.45;
      addBox("reveal-park-ground", { width: 14, height: 0.14, depth: 14 }, new Vector3(0, -0.07, 0), "#658455", false);
      addBox("reveal-gravel-path", { width: 2.8, height: 0.08, depth: 14 }, new Vector3(0, 0.04, 0), "#969080", false);
      addGravelDetail();
      addTree("reveal-park-tree-a", new Vector3(-4.2, 0, -3.4), "#4d7650");
      addTree("reveal-park-tree-b", new Vector3(4.5, 0, 2.9), "#668c59");
      addTree("reveal-park-tree-c", new Vector3(-4.6, 0, 3.8), "#577f4f");
      addBox("reveal-bench-seat", { width: 1.8, height: 0.18, depth: 0.48 }, new Vector3(2.8, 0.62, 0.7), "#8d5d35");
      addBox("reveal-bench-back", { width: 1.8, height: 0.75, depth: 0.14 }, new Vector3(2.8, 0.94, 0.95), "#8d5d35");
    } else if (sceneId === "scene.fallen_leaf_forest_path.v1") {
      this.sceneExplorationHalfExtent = 6.45;
      addBox("reveal-forest-ground", { width: 14, height: 0.14, depth: 14 }, new Vector3(0, -0.07, 0), "#536345", false);
      addBox("reveal-leaf-path", { width: 2.8, height: 0.08, depth: 14 }, new Vector3(0, 0.04, 0), "#8d613d", false);
      addLeafDetail();
      addTree("reveal-forest-tree-a", new Vector3(-4.2, 0, -3.6), "#b56b32");
      addTree("reveal-forest-tree-b", new Vector3(4.1, 0, -2.3), "#8e4933");
      addTree("reveal-forest-tree-c", new Vector3(-4.5, 0, 3.6), "#b18b3d");
      addTree("reveal-forest-tree-d", new Vector3(4.6, 0, 3.2), "#9a6334");
    } else {
      root.dispose(false, true);
      this.sceneRevealRoot = null;
      throw new Error(`Unknown scene reveal builder: ${sceneId}`);
    }
    this.resetParticipant();
    if (this.camera === this.getParticipantCamera()) this.camera.rotation.set(0, 0, 0);
    return { sceneId, meshCount: root.getChildMeshes().length, researchEligible: false };
  }

  getRevealMeshCount(): number {
    return this.content.getActiveMeshCount();
  }

  getEntranceAuditDescriptors(): readonly EntranceAuditDescriptor[] {
    return DIRECTIONS.map((direction) => {
      const gate = this.gates.get(direction);
      const material = this.gateMaterials.get(direction);
      if (!gate || !material) throw new Error(`Missing gate audit data for ${direction}`);
      return {
        direction,
        width: 2,
        height: 2.6,
        depth: 0.13,
        radialDistance: Math.hypot(gate.position.x, gate.position.z),
        scaleX: gate.scaling.x,
        scaleY: gate.scaling.y,
        scaleZ: gate.scaling.z,
        alpha: material.alpha,
        enabled: gate.isEnabled(),
      };
    });
  }

  async captureEntranceScreenshotSignatures(): Promise<readonly ScreenshotSignature[]> {
    const savedPosition = this.camera.position.clone();
    const savedRotation = this.camera.rotation.clone();
    const yaws: readonly number[] = [0, Math.PI / 2, Math.PI, -Math.PI / 2];
    const signatures: ScreenshotSignature[] = [];
    const canvas = this.scene.getEngine().getRenderingCanvas();
    if (!canvas) throw new Error("Rendering canvas is unavailable for entrance audit");
    try {
      this.camera.position.set(0, 1.65, 0);
      this.camera.rotation.x = 0;
      for (const yaw of yaws) {
        this.camera.rotation.y = yaw;
        this.scene.render();
        const dataUrl = canvas.toDataURL("image/png");
        signatures.push(await analyzeScreenshotDataUrl(dataUrl));
      }
    } finally {
      this.camera.position.copyFrom(savedPosition);
      this.camera.rotation.copyFrom(savedRotation);
    }
    return signatures;
  }

  isHeadNearWall(): boolean {
    const camera = this.getParticipantCamera();
    const ray = new Ray(camera.position, camera.getDirection(Vector3.Forward()), 0.22);
    return Boolean(this.scene.pickWithRay(ray, (mesh) => mesh.checkCollisions)?.hit);
  }

  setMovementVignette(moving: boolean, nearWall: boolean): void {
    const configuration = this.scene.imageProcessingConfiguration;
    configuration.vignetteEnabled = moving || nearWall;
    configuration.vignetteColor = new Color4(0.01, 0.015, 0.02, 1);
    configuration.vignetteStretch = nearWall ? 0.1 : 0.24;
    configuration.vignetteWeight = nearWall ? 4.2 : 2.1;
  }

  private getParticipantCamera(): FreeCamera {
    return this.scene.activeCamera instanceof FreeCamera ? this.scene.activeCamera : this.camera;
  }

  private buildMaze(): void {
    const floorMaterial = this.material("floor", "#35434d");
    const wallMaterial = this.material("wall", "#71808a");
    const fogMaterial = this.material("fog", "#d6e5e9", 0.2);
    fogMaterial.disableLighting = true;
    fogMaterial.emissiveColor = Color3.FromHexString("#a9c1c8").scale(0.42);

    const centerFloor = MeshBuilder.CreateCylinder("junction-floor", { diameter: 6, height: 0.12, tessellation: 8 }, this.scene);
    centerFloor.position.y = -0.06;
    this.attach(centerFloor, this.mazeRoot, floorMaterial, true);

    for (let side = 0; side < 8; side += 1) {
      if (side % 2 === 0) continue;
      const angle = (side * Math.PI) / 4;
      const wall = MeshBuilder.CreateBox(`octagon-wall-${side}`, { width: 2.45, height: 2.6, depth: 0.16 }, this.scene);
      wall.position.set(Math.sin(angle) * 2.78, 1.3, Math.cos(angle) * 2.78);
      wall.rotation.y = angle;
      this.attach(wall, this.mazeRoot, wallMaterial, true);
    }

    for (const direction of DIRECTIONS) {
      const vector = DIRECTION_VECTORS[direction];
      const angle = Math.atan2(vector.x, vector.z);
      const floor = MeshBuilder.CreateBox(`corridor-floor-${direction}`, { width: 2, height: 0.1, depth: 7 }, this.scene);
      floor.position.copyFrom(vector.scale(6.5));
      floor.position.y = -0.05;
      floor.rotation.y = angle;
      this.attach(floor, this.mazeRoot, floorMaterial, true);

      for (const side of [-1, 1]) {
        const wall = MeshBuilder.CreateBox(`corridor-wall-${direction}-${side}`, { width: 0.14, height: 2.6, depth: 7 }, this.scene);
        const lateral = new Vector3(vector.z, 0, -vector.x).scale(side);
        wall.position.copyFrom(vector.scale(6.5).add(lateral));
        wall.position.y = 1.3;
        wall.rotation.y = angle;
        this.attach(wall, this.mazeRoot, wallMaterial, true);
      }

      const gateMaterial = this.material(`gate-${direction}`, "#83909a", 0.88);
      const gate = MeshBuilder.CreateBox(`gate-${direction}`, { width: 2, height: 2.6, depth: 0.13 }, this.scene);
      gate.position.copyFrom(vector.scale(3));
      gate.position.y = 1.3;
      gate.rotation.y = angle;
      this.attach(gate, this.mazeRoot, gateMaterial, true);
      this.gates.set(direction, gate);
      this.gateMaterials.set(direction, gateMaterial);

      const photoMaterial = new StandardMaterial(`decision-photo-${direction}-material`, this.scene);
      photoMaterial.backFaceCulling = false;
      photoMaterial.specularColor = Color3.Black();
      const photo = MeshBuilder.CreatePlane(`decision-photo-${direction}`, { width: 1.76, height: 1.08 }, this.scene);
      photo.position.copyFrom(vector.scale(2.91));
      photo.position.y = 1.43;
      photo.rotation.y = angle;
      photo.material = photoMaterial;
      photo.parent = this.mazeRoot;
      photo.isPickable = false;
      photo.setEnabled(false);
      this.photoPlanes.set(direction, photo);
      this.photoMaterials.set(direction, photoMaterial);

      const fog = MeshBuilder.CreateBox(`fog-link-${direction}`, { width: 1.86, height: 2.5, depth: 1.5 }, this.scene);
      fog.position.copyFrom(vector.scale(9.15));
      fog.position.y = 1.25;
      fog.rotation.y = angle;
      this.attach(fog, this.mazeRoot, fogMaterial, false);
    }
  }

  private buildInstructionRoom(): void {
    const wallMaterial = this.material("instruction-wall", "#5e707b");
    const floor = MeshBuilder.CreateCylinder("instruction-floor", { diameter: 7, height: 0.12, tessellation: 6 }, this.scene);
    floor.position.set(0, -0.06, -20);
    this.attach(floor, this.instructionRoot, this.material("instruction-floor", "#2f414c"), true);

    for (let side = 0; side < 6; side += 1) {
      const angle = (side * Math.PI) / 3;
      const wall = MeshBuilder.CreateBox(`instruction-wall-${side}`, { width: 3.55, height: 2.8, depth: 0.12 }, this.scene);
      wall.position.set(Math.sin(angle) * 3.08, 1.4, -20 + Math.cos(angle) * 3.08);
      wall.rotation.y = angle;
      this.attach(wall, this.instructionRoot, wallMaterial, true);
    }

    const panels = [
      ["1  听", "注视方向，按 Space 试听"],
      ["2  选", "按 E 选中，再按 E 确认"],
      ["3  走", "正确后穿过开启的通道"],
    ] as const;
    panels.forEach(([title, body], index) => {
      const panel = MeshBuilder.CreatePlane(`instruction-panel-${index}`, { width: 1.75, height: 2.15 }, this.scene);
      panel.position.set((index - 1) * 2.05, 1.55, -17.03);
      const texture = new DynamicTexture(`instruction-texture-${index}`, { width: 512, height: 640 }, this.scene, false);
      const context = texture.getContext();
      context.fillStyle = "#18242d";
      context.fillRect(0, 0, 512, 640);
      const parts = body.split("，");
      texture.drawText(title, null, 175, "bold 64px Segoe UI, sans-serif", "#8ce1e8", null, true, false);
      texture.drawText(parts[0] ?? "", null, 330, "32px Segoe UI, sans-serif", "#d9e5eb", null, true, false);
      texture.drawText(parts[1] ?? "", null, 385, "32px Segoe UI, sans-serif", "#d9e5eb", null, true, true);
      const material = new StandardMaterial(`instruction-panel-material-${index}`, this.scene);
      material.diffuseTexture = texture;
      material.emissiveColor = Color3.White();
      material.backFaceCulling = false;
      panel.material = material;
      panel.parent = this.instructionRoot;
    });
  }

  private refreshGateEmissive(): void {
    this.gateMaterials.forEach((material, direction) => {
      const emphasis = direction === this.pending ? 0.8 : direction === this.focused ? 0.48 : 0.16;
      material.emissiveColor = material.diffuseColor.scale(emphasis);
    });
  }

  private disposeReveal(): void {
    this.content.clear();
    this.disposeSceneReveal();
  }

  private disposeSceneReveal(): void {
    this.sceneRevealRoot?.dispose(false, true);
    this.sceneRevealRoot = null;
    this.sceneExplorationHalfExtent = null;
  }

  private material(name: string, hex: string, alpha = 1): StandardMaterial {
    const material = new StandardMaterial(`${name}-material`, this.scene);
    material.diffuseColor = Color3.FromHexString(hex);
    material.roughness = 0.9;
    material.alpha = alpha;
    return material;
  }

  private attach(
    mesh: Mesh,
    root: TransformNode,
    material: StandardMaterial,
    collision: boolean,
  ): void {
    mesh.parent = root;
    mesh.material = material;
    mesh.checkCollisions = collision;
    mesh.isPickable = collision;
  }
}
